<script lang="ts">
/**
 * Horizontal row of filter facets for list pages.
 *
 * Each facet is a USelectMenu dropdown (tag, type, container) or a
 * toggle button (needs attention). Parent handles navigation on change.
 *
 * @example
 * ```vue
 * <FilterBar
 *   :tags="available_tags"
 *   :types="available_types"
 *   :containers="available_containers"
 *   :active-tag="tag_filter"
 *   @update:tag="(v) => navigate({ tag: v })"
 * />
 * ```
 */
export interface FilterBarProps {
  tags: string[]
  types: { id: string; name: string }[]
  containers: { id: string; name: string; path: string | null }[]
  activeTag: string | null
  activeTypeId: string | null
  activeContainerId: string | null
  attentionActive: boolean
}
</script>

<script setup lang="ts">
import { computed } from "vue"

import type { SelectOption } from "@/types/models"

const props = defineProps<FilterBarProps>()
const emit = defineEmits<{
  "update:tag": [value: string | null]
  "update:typeId": [value: string | null]
  "update:containerId": [value: string | null]
  "update:attention": [value: boolean]
}>()

const tagItems = computed<SelectOption[]>(() => props.tags.map((t) => ({ label: t, value: t })))

const typeItems = computed<SelectOption[]>(() =>
  props.types.map((t) => ({ label: t.name, value: t.id })),
)

const containerItems = computed<SelectOption[]>(() =>
  props.containers.map((c) => ({
    label: c.path ? `${c.path} > ${c.name}` : c.name,
    value: c.id,
  })),
)

function onTagChange(value: string) {
  emit("update:tag", value === props.activeTag ? null : value)
}

function onTypeChange(value: string) {
  emit("update:typeId", value === props.activeTypeId ? null : value)
}

function onContainerChange(value: string) {
  emit("update:containerId", value === props.activeContainerId ? null : value)
}
</script>

<template>
  <div class="flex flex-wrap items-center gap-2">
    <USelectMenu
      :items="tagItems"
      :model-value="activeTag ?? undefined"
      value-key="value"
      placeholder="Tag"
      :search-input="tagItems.length > 8"
      :ui="{ content: 'min-w-fit' }"
      color="neutral"
      variant="subtle"
      size="sm"
      @update:model-value="onTagChange"
    />

    <USelectMenu
      :items="typeItems"
      :model-value="activeTypeId ?? undefined"
      value-key="value"
      placeholder="Type"
      :search-input="false"
      :ui="{ content: 'min-w-fit' }"
      color="neutral"
      variant="subtle"
      size="sm"
      @update:model-value="onTypeChange"
    />

    <USelectMenu
      :items="containerItems"
      :model-value="activeContainerId ?? undefined"
      value-key="value"
      placeholder="Container"
      :ui="{ content: 'min-w-fit' }"
      color="neutral"
      variant="subtle"
      size="sm"
      @update:model-value="onContainerChange"
    />

    <UButton
      label="Needs attention"
      :color="attentionActive ? 'primary' : 'neutral'"
      variant="subtle"
      size="sm"
      @click="emit('update:attention', !attentionActive)"
    />
  </div>
</template>
